import { array, monoid, number, ord, string } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useCallback } from 'react';
import { Task, useRepositoryMutation, useRepositoryQuery } from '@mews-commander/data-access';
import {
    DateTimeService,
    DateTimeUtcService,
    showSuccessAlert,
    useTimezone,
    lt,
    AppClockService,
    useAppClockService,
} from '@mews/framework';

const temporalStateOrdering = { Missed: 0, Pending: 1, Future: 2, Closed: 3 } as const;
export type TaskTemporalState = ReturnType<typeof getTaskTemporalState>;
const getTaskTemporalState = (
    deadlineUtc: string,
    closedUtc: string | null | undefined,
    nowUtc: string,
    endOfTodayUtc: string
) => {
    if (closedUtc) {
        return 'Closed';
    }

    if (deadlineUtc <= nowUtc) {
        return 'Missed';
    }

    if (deadlineUtc > endOfTodayUtc) {
        return 'Future';
    }

    return 'Pending';
};
export interface ExtendedTask extends Task {
    taskTemporalState: TaskTemporalState;
}

const sortTasks = monoid.concatAll(ord.getMonoid<ExtendedTask>())([
    ord.contramap((task: ExtendedTask) => temporalStateOrdering[task.taskTemporalState])(number.Ord),
    ord.contramap((task: ExtendedTask) => task.deadlineUtc)(string.Ord),
]);

const selectTasks = (tasks: Task[], appClockService: AppClockService, timezone: string) => {
    const appNowUtc = appClockService.getAppNowUtc();
    const endOfTodayUtc = pipe(
        DateTimeService.fromUtc(appNowUtc, timezone),
        DateTimeService.endOfDay,
        DateTimeService.toUtc,
        DateTimeUtcService.toISOString
    );

    return pipe(
        tasks,
        array.map((task): ExtendedTask => {
            return {
                ...task,
                taskTemporalState: getTaskTemporalState(
                    task.deadlineUtc,
                    task.closedUtc,
                    DateTimeUtcService.toISOString(appNowUtc),
                    endOfTodayUtc
                ),
            };
        }),
        array.sort(sortTasks)
    );
};
export const useTasksQuery = (reservationIds: string[]) => {
    const appClockService = useAppClockService();
    const timezone = useTimezone();

    const tasksQuery = useRepositoryQuery(
        'tasks',
        'getAll',
        { serviceOrderIds: reservationIds },
        {
            select: useCallback(
                (result) => (result.tasks ? selectTasks(result.tasks, appClockService, timezone) : []),
                [appClockService, timezone]
            ),
        }
    );
    return tasksQuery;
};

export const useTaskCloseMutation = () =>
    useRepositoryMutation('tasks', 'close', {
        invalidateQueries: { tasks: ['getAll'] },
        onSuccess: () => showSuccessAlert({ description: lt('TaskCloseSuccessMessage') }),
    });
